import { createRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { meLayoutRoute } from './meLayout.js';
import type { CurrencyCode } from '@kora/types';
import { createEwaDraw, currentPeriod, getEwaQuote, listEwaDraws, type EwaDraw } from '../api/ewaDraws.js';
import { formatDate, formatMoney } from '../lib/format.js';
import { ApiError } from '../api/client.js';
import { Alert } from '../components/Alert.js';

/**
 * /me/ewa-draws — Earned Wage Access (ADR-0009 D9, Phase 3b). Shows wages
 * earned-to-date this period and the available-to-draw figure (already net of
 * the minimum-wage floor, any loan installment, and the employer policy cap),
 * lets the employee take a draw, and lists their draw history. The draw is
 * recorded; finance disburses out of band (v1 — no payout rail).
 */
export const myEwaDrawsRoute = createRoute({
  getParentRoute: () => meLayoutRoute,
  path: '/me/ewa-draws',
  component: MyEwaDrawsPage,
});

function MyEwaDrawsPage(): JSX.Element {
  const qc = useQueryClient();
  const period = currentPeriod();
  const [amount, setAmount] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [lastDraw, setLastDraw] = useState<string | null>(null);

  const quote = useQuery({ queryKey: ['me-ewa-quote', period.periodStart], queryFn: () => getEwaQuote(period) });
  const draws = useQuery({ queryKey: ['me-ewa-draws'], queryFn: listEwaDraws });

  const take = useMutation({
    mutationFn: () => createEwaDraw({ amount, ...period }),
    onSuccess: async (r) => {
      setFormError(null);
      setLastDraw(`Drew ${formatMoney(r.amount, r.currency as CurrencyCode)}. Finance will disburse shortly; it nets in your next payroll run.`);
      setAmount('');
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['me-ewa-draws'] }),
        qc.invalidateQueries({ queryKey: ['me-ewa-quote', period.periodStart] }),
      ]);
    },
    onError: (e: unknown) => {
      setLastDraw(null);
      setFormError(e instanceof ApiError ? e.message : 'Could not record your draw.');
    },
  });

  const available = quote.data ? Number(quote.data.availableToDraw) : 0;
  const quoteUnavailable = quote.isError;

  return (
    <section className="p-8 space-y-6 max-w-3xl">
      <header className="space-y-2">
        <h1 className="font-serif text-3xl">Earned wage access</h1>
        <p className="text-sm text-gray-400">Draw a portion of the wages you&rsquo;ve already earned this period, before payday. It&rsquo;s your own earned pay — no interest, no fees.</p>
      </header>

      <div className="rounded border border-gray-900 p-5 space-y-4">
        {quote.isLoading ? <p className="text-gray-500 text-sm">Loading your earned-to-date…</p> : null}
        {quoteUnavailable ? (
          <Alert variant="warning">
            {quote.error instanceof ApiError ? quote.error.message : 'Earned wage access isn’t available for your account.'}
          </Alert>
        ) : null}

        {quote.data ? (
          <>
            <dl className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <dt className="text-gray-500 text-xs">Earned to date</dt>
                <dd className="text-lg text-gray-200">{formatMoney(quote.data.earnedToDate, quote.data.currency as CurrencyCode)}</dd>
              </div>
              <div>
                <dt className="text-gray-500 text-xs">Available to draw</dt>
                <dd className="text-lg text-white">{formatMoney(quote.data.availableToDraw, quote.data.currency as CurrencyCode)}</dd>
              </div>
            </dl>
            {Number(quote.data.reservedLoanInstallment) > 0 ? (
              <p className="text-xs text-gray-500">Your available amount already sets aside this period&rsquo;s loan repayment ({formatMoney(quote.data.reservedLoanInstallment, quote.data.currency as CurrencyCode)}).</p>
            ) : null}

            {lastDraw ? <div role="status" className="rounded border border-green-900 bg-green-950/40 px-3 py-2 text-sm text-green-300">{lastDraw}</div> : null}
            {formError ? <Alert variant="error">{formError}</Alert> : null}

            <div className="flex items-end gap-3">
              <label className="flex-1">
                <span className="block text-xs text-gray-500 mb-1">Amount to draw ({quote.data.currency})</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-full rounded border border-gray-700 bg-black px-3 py-2 text-sm font-mono"
                />
              </label>
              <button
                type="button"
                onClick={() => take.mutate()}
                disabled={take.isPending || available <= 0 || !amount}
                className="rounded bg-white text-black px-4 py-2 text-sm font-medium hover:bg-gray-200 disabled:opacity-50"
              >
                {take.isPending ? 'Drawing…' : 'Draw'}
              </button>
            </div>
            {available <= 0 ? <p className="text-xs text-gray-500">Nothing available to draw right now.</p> : null}
          </>
        ) : null}
      </div>

      <DrawHistory draws={draws.data?.draws ?? []} loading={draws.isLoading} />
    </section>
  );
}

function DrawHistory({ draws, loading }: { draws: EwaDraw[]; loading: boolean }): JSX.Element {
  if (loading) return <div className="rounded border border-gray-900 p-6 text-center text-gray-500 text-sm">Loading…</div>;
  if (draws.length === 0) return <section className="rounded border border-dashed border-gray-800 p-8 text-center text-sm text-gray-400">No draws yet this period.</section>;
  return (
    <div className="rounded border border-gray-900 overflow-hidden">
      <header className="px-4 py-3 text-xs uppercase tracking-wide text-gray-400 border-b border-gray-900">Your draws</header>
      <table className="w-full text-sm">
        <thead className="bg-gray-950 text-xs uppercase tracking-wide text-gray-500">
          <tr>
            <th className="text-left px-4 py-2">Taken</th>
            <th className="text-left px-4 py-2">Amount</th>
            <th className="text-left px-4 py-2">Status</th>
            <th className="text-left px-4 py-2">Remaining</th>
          </tr>
        </thead>
        <tbody>
          {draws.map((d) => (
            <tr key={d.id} className="border-t border-gray-900">
              <td className="px-4 py-3 text-gray-400">{formatDate(d.createdAt)}</td>
              <td className="px-4 py-3 text-gray-200">{formatMoney(d.amount, d.currency as CurrencyCode)}</td>
              <td className="px-4 py-3 text-gray-300">{d.status}</td>
              <td className="px-4 py-3 text-gray-400">{formatMoney(d.remainingBalance, d.currency as CurrencyCode)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
